// Admin onboarding tour steps
export const adminOnboardingSteps = [
  {
    title: "Welcome to PPMT Portal! 🎉",
    content: "Welcome to your client management portal. Let's take a quick tour to help you get started and make the most of all the features available to you.",
    icon: "👋",
    placement: "center",
    tips: "You can restart this tour anytime from the Help menu."
  },
  {
    title: "Dashboard Overview",
    target: "[data-testid='dashboard-title']",
    content: "Your dashboard gives you a quick snapshot of your business - total clients, files, unread messages, and recent activity. Click on any card to navigate directly to that section.",
    icon: "📊",
    placement: "bottom",
    tips: "Keep an eye on the Recent Activity widget for important updates."
  },
  {
    title: "Client Management",
    target: "a[href='/admin/clients']",
    content: "Manage all your clients here. You can add new clients, import from CSV, send portal invitations, archive/suspend clients, and access their detailed profiles.",
    icon: "👥",
    placement: "right",
    tips: "Use bulk actions to manage multiple clients at once - select them and use Archive, Suspend, or Delete."
  },
  {
    title: "File Management",
    target: "a[href='/admin/files']",
    content: "Upload, organize, and share documents with your clients. Files can be tagged for easy searching and you can control which files are visible to clients.",
    icon: "📁",
    placement: "right",
    tips: "Use the signature request feature to get documents signed electronically."
  },
  {
    title: "Messages",
    target: "a[href='/admin/messages']",
    content: "Communicate securely with your clients through the built-in messaging system. All messages are saved and organized by client for easy reference.",
    icon: "💬",
    placement: "right"
  },
  {
    title: "Invoicing",
    target: "a[href='/admin/invoicing']",
    content: "Create and send professional invoices to your clients. Track payment status and manage your billing from one central location.",
    icon: "💰",
    placement: "right",
    tips: "Set up your Stripe integration in Settings to accept online payments."
  },
  {
    title: "Team Management",
    target: "a[href='/admin/team']",
    content: "Add team members and control their access levels. Sub-admins can have customized permissions to access only what they need.",
    icon: "👔",
    placement: "right",
    tips: "New team members receive an email with their login credentials automatically."
  },
  {
    title: "Settings & Configuration",
    target: "a[href='/admin/settings']",
    content: "Configure your portal settings including email templates, notification preferences, and view activity logs for audit purposes.",
    icon: "⚙️",
    placement: "right",
    tips: "Check the Activity Logs tab to monitor all actions taken in your portal."
  },
  {
    title: "You're All Set! 🚀",
    content: "You're ready to start managing your clients efficiently. If you need help, check our documentation or contact support. Good luck!",
    icon: "🎯",
    placement: "center",
    tips: "Pro tip: Invite your first client by going to Clients → Add Client and checking 'Send invitation email'."
  }
];

// Client onboarding tour steps
export const clientOnboardingSteps = [
  {
    title: "Welcome to Your Portal! 🎉",
    content: "Welcome to your secure client portal. Here you can access your documents, communicate with your advisor, and manage your account. Let's show you around!",
    icon: "👋",
    placement: "center",
    tips: "Bookmark this page for easy access in the future."
  },
  {
    title: "Your Dashboard",
    target: "[data-testid='client-dashboard']",
    content: "This is your personal dashboard. You'll see important updates, pending actions like documents to sign, and quick access to all your information.",
    icon: "🏠",
    placement: "bottom"
  },
  {
    title: "Your Documents",
    target: "a[href='/documents']",
    content: "Access all documents shared with you here. You can view, download, and see documents that need your signature.",
    icon: "📄",
    placement: "right",
    tips: "Documents requiring your signature will show a notification badge."
  },
  {
    title: "Messages",
    target: "a[href='/messages']",
    content: "Communicate securely with your advisor through the messaging system. All conversations are saved for your reference.",
    icon: "💬",
    placement: "right",
    tips: "You'll receive email notifications when you have new messages."
  },
  {
    title: "Invoices & Payments",
    target: "a[href='/invoices']",
    content: "View your invoices and make payments online. You can see your payment history and download invoices for your records.",
    icon: "💳",
    placement: "right"
  },
  {
    title: "Notifications",
    target: "[data-testid='notification-bell']",
    content: "The notification bell shows you important alerts like documents to sign or new messages. A red badge means you have pending items.",
    icon: "🔔",
    placement: "bottom",
    tips: "Click the bell to see all your notifications at once."
  },
  {
    title: "Your Profile",
    target: "a[href='/profile']",
    content: "Update your personal information, change your password, and manage your account settings from your profile page.",
    icon: "👤",
    placement: "right",
    tips: "Keep your contact information up to date so we can reach you when needed."
  },
  {
    title: "You're Ready! 🚀",
    content: "That's everything you need to know to get started. If you have any questions, use the Messages feature to contact your advisor. Welcome aboard!",
    icon: "🎯",
    placement: "center",
    tips: "Check your dashboard regularly for updates and pending actions."
  }
];

export default { adminOnboardingSteps, clientOnboardingSteps };
